import { memo, useMemo, Fragment } from "react";
import PropTypes from "prop-types";
import { Index } from "react-instantsearch";

import { PAGE_MODULES, NON_RENDER_COUNT_MODULES } from "enums/pageModules";
import Banner from "components/Banner";
import AlgoliaCards from "components/AlgoliaCards";
import CategoryCollection from "components/CategoryCollection";
import CategorySubMenu from "components/CategorySubMenu";
import CategoryRow from "components/CategoryRow";
import AlgoliaCardsPresenter from "presenters/AlgoliaCardsPresenter";
import HeroPresenter from "presenters/HeroPresenter";
import CategoryCollectionPresenter from "presenters/CategoryCollectionPresenter";
import FeaturePanelPresenter from "presenters/FeaturePanelPresenter";
import CategoryRowPresenter from "presenters/CategoryRowPresenter";
import CategoryFeaturePanel from "components/CategoryFeaturePanel";
import AlgoliaSearchWrapper from "components/AlgoliaSearchWrapper";
import { useSettings, useAlgolia, usePageModules } from "hooks";
import withAlgoliaCard from "components/withAlgoliaCard";
import { useUsers } from "hooks";

import LoadingPlaceholder from "./components/LoadingPlaceholder";
import { StyledCategoryRowContainer, StyledFeaturePanelContainer, StyledModuleMenuContainer } from "./styles";

const CategoryRowWithAlgoliaCard = withAlgoliaCard(CategoryRow);
const CategoryFeaturePanelWithAlgoliaCard = withAlgoliaCard(CategoryFeaturePanel);
const stalledSearchDelayTime = Number(import.meta.env.VITE_CATEGORY_PAGE_ALGOLIA_STALLED_TIME) || 1000;

const PageModules = memo(({ modules = [], loading, context = "", children }) => {
  const {
    settings: { algolia },
  } = useSettings();
  const { createInitialUiStateFromMultiple } = useAlgolia();
  const { getPageModulesConfiguration } = usePageModules();
  const { currentUserLoaded } = useUsers();
  const initialUiState = useMemo(() => {
    if (!modules?.length || !currentUserLoaded) return undefined;
    const modulesWithExtraConfig = getPageModulesConfiguration(modules);

    return createInitialUiStateFromMultiple(modulesWithExtraConfig);
  }, [loading, getPageModulesConfiguration, modules, currentUserLoaded]);

  const renderAlgoliaCards = (module, key, modulePosition) => {
    return (
      <Index key={key} indexName={algolia.indexName} indexId={module?.id}>
        <AlgoliaCards
          loading={loading}
          algoliaCard={module}
          showDetails
          modulePosition={modulePosition}
          initialQueryObject={initialUiState?.[module?.id]}
        />
      </Index>
    );
  };

  const renderCategoryCollection = (module, key, modulePosition) => {
    return (
      <CategoryCollection
        key={key}
        loading={loading}
        categories={CategoryCollectionPresenter.categories(module)}
        modulePosition={modulePosition}
      />
    );
  };

  const renderHero = (module, key) => {
    return <Banner key={key} hero={module} loading={loading} />;
  };

  const renderModuleMenu = (module, key) => {
    const menuItems = module.menuItems.data.map((item) => item.attributes);

    return (
      <StyledModuleMenuContainer key={key}>
        <CategorySubMenu menuItems={menuItems} />
      </StyledModuleMenuContainer>
    );
  };

  const renderCategoryRow = (module, key, modulePosition) => {
    const title = CategoryRowPresenter.title(module);
    const isTopRanking = CategoryRowPresenter.isTopRanking(module);

    return (
      <StyledCategoryRowContainer key={key}>
        <Index indexName={algolia.indexName} indexId={module?.id}>
          <CategoryRowWithAlgoliaCard
            loading={loading}
            initialQueryObject={initialUiState?.[module?.id]}
            title={title}
            modulePosition={modulePosition}
            isTopRanking={isTopRanking}
          />
        </Index>
      </StyledCategoryRowContainer>
    );
  };

  const renderFeaturedPanel = (module, key, modulePosition) => {
    const algoliaCard = FeaturePanelPresenter.algoliaCardFromCards(module);
    const sharedProps = {
      loading,
      autoplayDelay: FeaturePanelPresenter.transitionInSeconds(module),
      modulePosition,
    };

    return (
      <StyledFeaturePanelContainer key={key}>
        {algoliaCard ? (
          <Index indexName={algolia.indexName} indexId={module?.id}>
            <CategoryFeaturePanelWithAlgoliaCard
              key={key}
              loading={loading}
              initialQueryObject={initialUiState?.[module?.id]}
              modulePosition={modulePosition}
              {...sharedProps}
            />
          </Index>
        ) : (
          <CategoryFeaturePanel key={key} items={FeaturePanelPresenter.cards(module)} {...sharedProps} />
        )}
      </StyledFeaturePanelContainer>
    );
  };

  const renderModule = (module, moduleRenderCount) => {
    const { id, type } = module;

    const moduleComponents = {
      [PAGE_MODULES.HERO]: () => renderHero(module, id),
      [PAGE_MODULES.ALGOLIA_CARDS]: () => renderAlgoliaCards(module, id, moduleRenderCount),
      [PAGE_MODULES.CATEGORY_COLLECTIONS]: () => renderCategoryCollection(module, id, moduleRenderCount),
      [PAGE_MODULES.MODULE_MENU]: () => renderModuleMenu(module, id),
      [PAGE_MODULES.CATEGORY_ROW]: () => renderCategoryRow(module, id, moduleRenderCount),
      [PAGE_MODULES.MODULE_SLIDER]: () => renderFeaturedPanel(module, id, moduleRenderCount),
    };

    return moduleComponents[type] ? moduleComponents[type]() : null;
  };

  const renderedModules = () => {
    let renderCount = 0;

    return modules.map((module, index) => {
      const { id, type } = module;

      if (!NON_RENDER_COUNT_MODULES.has(type)) renderCount += 1;

      return <Fragment key={id ?? index}>{renderModule(module, renderCount)}</Fragment>;
    });
  };

  if (loading || !modules.length || !currentUserLoaded) return <LoadingPlaceholder />;

  return (
    <AlgoliaSearchWrapper
      stalledSearchDelayTime={stalledSearchDelayTime}
      proxy={false}
      context={context}
      initialUiState={loading ? undefined : initialUiState}
    >
      {renderedModules()}
      {children}
    </AlgoliaSearchWrapper>
  );
});

PageModules.propTypes = {
  modules: PropTypes.arrayOf(
    PropTypes.oneOfType([AlgoliaCardsPresenter.shape(), HeroPresenter.shape(), CategoryCollectionPresenter.shape()]),
  ),
  loading: PropTypes.bool,
  context: PropTypes.string,
  children: PropTypes.node,
};

export default PageModules;
